import './styles/global.css';
import './styles/ads.css';

import { GAME_REGISTRY } from './engine/GameRegistry';
import { gameLoader } from './engine/GameLoader';
import { InterstitialAd } from './ads/InterstitialAd';
import { EventBus } from './core/events/EventBus';
import { ScoreValidator } from './security/ScoreValidator';
import { scoreSubmitLimiter } from './security/RateLimiter';
import { AudioManager } from './engine/audio/AudioManager';
import { BackgroundRadio } from './engine/audio/BackgroundRadio';
import { AuthStore } from './core/store/authStore';
import { AuthService } from './services/AuthService';
import { FriendService } from './services/FriendService';
import { FriendModal } from './ui/components/FriendModal';
import { ToastManager } from './ui/components/Toast';
import { CookieBanner } from './ui/components/CookieBanner';
import { ApiClient } from './services/ApiClient';
import { DuelInviteModal } from './ui/components/DuelInviteModal';
import { DuelResultModal } from './ui/components/DuelResultModal';
import { DuelService } from './services/DuelService';
import { NavBar } from './ui/components/NavBar';
import { GameControls } from './ui/components/GameControls';
import { LeaderboardView } from './ui/views/LeaderboardView';
import { ProfileView } from './ui/views/ProfileView';

import { PrivacyView } from './ui/views/PrivacyView';
import { NicheHubView } from './ui/views/NicheHubView';
import { AdminView } from './ui/views/AdminView';
import { SeoService } from './services/SeoService';
import { AnalyticsService } from './services/AnalyticsService';

const scoreValidator = new ScoreValidator();

class App {
  private appEl: HTMLElement;
  private currentView: 'lobby' | 'game' | 'niche' | 'leaderboard' | 'profile' | 'privacy' | 'admin' = 'lobby';
  private selectedCategory = 'all';
  private catalogPage = 1;
  private readonly catalogPageSize = 10;
  private sortBy: 'popular' | 'newest' | 'alphabetical' = 'popular';
  private searchQuery = '';
  private pendingScore: { gameId: string; score: number; sessionMs?: number } | null = null;
  private gameControls: GameControls | null = null;
  private activeGameId: string | null = null;

  constructor() {
    const el = document.getElementById('app');
    if (!el) throw new Error('App root element not found');
    this.appEl = el;

    this.initEventListeners();
    this.initVisibilityListener();
    this.initGlobalErrorHandler();
  }

  private initGlobalErrorHandler(): void {
    window.addEventListener('error', (e) => {
      ApiClient.logSystemError(e.message, e.error?.stack || '', window.location.href);
    });
    window.addEventListener('unhandledrejection', (e) => {
      ApiClient.logSystemError(e.reason?.message || 'Unhandled Promise Rejection', e.reason?.stack || '', window.location.href);
    });
  }

  init(): void {
    AuthStore.checkGuestExpiration();
    AuthService.initAuth();
    DuelInviteModal.init();
    CookieBanner.init();
    BackgroundRadio.autoStart();
    let lastUserId = AuthStore.getUser()?.id;

    AuthStore.subscribe(async (user) => {
      if (user && this.pendingScore) {
        const { gameId, score, sessionMs } = this.pendingScore;
        const res = await ApiClient.submitScore(user.id, gameId, score, sessionMs);
        if (!res.error) {
          ToastManager.show(`🎉 Askıdaki skorunuz (${score}) hesabınıza eklendi!`, 'success');
        }
        this.pendingScore = null;
      }
      
      const newUserId = user?.id;
      if (lastUserId !== newUserId) {
        lastUserId = newUserId;
        // Do not interrupt active gameplay for auth changes
        if (this.currentView !== 'game') {
          this.render();
        }
      }
    });
    this.render();
    this.renderGlobalBugReporter();
  }

  private renderGlobalBugReporter(): void {
    const styleId = 'global-bug-btn-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .bug-reporter-btn {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 9999;
          background: #ef4444;
          color: #fff;
          border: none;
          border-radius: 30px;
          height: 54px;
          width: 54px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          overflow: hidden;
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s, transform 0.2s;
          padding: 0;
        }
        .bug-reporter-btn:hover, .bug-reporter-btn:focus {
          width: 190px;
          background: #dc2626;
        }
        .bug-reporter-icon {
          min-width: 54px;
          height: 54px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        .bug-reporter-text {
          white-space: nowrap;
          font-weight: 700;
          font-size: 0.95rem;
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
          padding-right: 16px;
        }
        .bug-reporter-btn:hover .bug-reporter-text,
        .bug-reporter-btn:focus .bug-reporter-text {
          opacity: 1;
          transition-delay: 0.1s;
        }
        .bug-reporter-btn:active {
          transform: scale(0.95);
        }
      `;
      document.head.appendChild(style);
    }

    const btn = document.createElement('button');
    btn.className = 'bug-reporter-btn';
    btn.innerHTML = `
      <div class="bug-reporter-icon">🐞</div>
      <div class="bug-reporter-text">Hata/Öneri Bildir</div>
    `;
    btn.onclick = () => this.showBugReportModal();
    document.body.appendChild(btn);
  }

  private initEventListeners(): void {
    EventBus.on('game:scoreSubmit', async ({ gameId, score, sessionMs }) => {
      console.log(`[ScoreSubmit] Game: ${gameId}, Score: ${score}, Time: ${sessionMs}ms`);

      if (score <= 0) {
        return; // Sessizce iptal et (hızlı yeniden başlatmalardaki hatayı gizlemek için)
      }

      if (!scoreSubmitLimiter.check()) {
        ToastManager.show('Çok hızlı skor gönderdiniz. Lütfen bekleyin.', 'warning');
        return;
      }

      const validation = scoreValidator.validate(score, sessionMs);
      if (!validation.valid) {
        console.warn(`[AntiCheat] Reddedildi: ${validation.reason}`);
        AudioManager.playGameOverSound();
        ToastManager.show(`Skor doğrulanamadı: ${validation.reason}`, 'error');
        return;
      }

      AudioManager.playScoreSound();
      
      const user = AuthStore.getUser();
      if (!user) {
        // Guest mode
        this.pendingScore = { gameId, score, sessionMs };
        this.showGuestScoreModal(score);
        return;
      }

      // Logged in
      const res = await ApiClient.submitScore(user.id, gameId, score, sessionMs);
      if (res.error) {
        ToastManager.show('Skor kaydedilemedi.', 'error');
      } else {
        ToastManager.show(`🎉 Tebrikler! Skorunuz başarıyla kaydedildi: ${score}`, 'success');
      }

      // Check if we were in a duel
      if (DuelService.activeDuel) {
        DuelService.sendLiveScore(score); // final sync
        import('./services/DuelService').then(async ({ DuelService }) => {
          // Tell opponent we finished
          await DuelService.sendDuelFinished(score);
          
          DuelResultModal.show(score);
        });
      }
    });

    EventBus.on('toast:show', ({ message, type }) => {
      ToastManager.show(message, type);
    });

    // ─── Duel Events ──────────────────────────────────────────────────────────
    EventBus.on('duel:start', (duel) => {
      this.activeGameId = duel.game_id;
      this.currentView = 'game';
      this.render();
      this.startGameFlow(duel.game_id);
    });

    EventBus.on('game:liveScoreUpdate', ({ score }) => {
      if (DuelService.activeDuel) {
        DuelService.sendLiveScore(score);
        const myScoreEl = document.getElementById('duel-my-score');
        if (myScoreEl) myScoreEl.innerText = score.toString();
      }
    });

    EventBus.on('duel:opponent_score', ({ score }) => {
      const oppScoreEl = document.getElementById('duel-opp-score');
      if (oppScoreEl) oppScoreEl.innerText = score.toString();
    });

    EventBus.on('duel:finished_by_opponent', (payload) => {
      // The opponent has finished playing. We don't forcefully stop the local player yet,
      // but we update the UI to show the opponent's final score and status.
      const oppScoreEl = document.getElementById('duel-opp-score');
      if (oppScoreEl) {
        oppScoreEl.innerText = `${payload.score} (Bitti)`;
        oppScoreEl.style.color = '#fbbf24';
      }
      ToastManager.show(`Rakibiniz oyunu ${payload.score} skorla tamamladı! Sizi bekliyor...`, 'info');
    });

    // Task 2: Stop keyboard events from propagating when typing in inputs/textareas
    const stopPropagationIfInput = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', stopPropagationIfInput, true);
    document.addEventListener('keyup', stopPropagationIfInput, true);
  }

  private initVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (this.currentView === 'game') {
        if (document.hidden) {
          gameLoader.pause();
          
          // Show overlay indicating paused state
          const overlay = document.getElementById('game-overlay');
          if (overlay) {
            overlay.style.display = 'flex';
            overlay.innerHTML = `
              <div style="background:rgba(15,23,42,0.9); padding:20px 40px; border-radius:12px; border:2px solid #334155; text-align:center;">
                <h2 style="color:#f8fafc; font-size:1.5rem; margin-bottom:8px;">⏸️ Oyun Duraklatıldı</h2>
                <p style="color:#94a3b8;">Oyuna dönmek için buraya tıklayın.</p>
              </div>
            `;
            overlay.onclick = () => {
              gameLoader.resume();
              overlay.style.display = 'none';
            };
          }
          ToastManager.show('Sekmeden ayrıldınız — oyun otomatik duraklatıldı.', 'info');
        }
      }
    });
  }

  private render(): void {
    this.appEl.innerHTML = '';

    // If playing game, render dedicated game view
    if (this.currentView === 'game') {
      this.renderGameView();
      return;
    }

    // SPA Layout: Header NavBar -> Main Content -> Footer
    const headerEl = NavBar.render(this.currentView, (tab) => {
      this.currentView = tab as any;
      this.render();
    });
    this.appEl.appendChild(headerEl);

    const mainEl = document.createElement('main');
    mainEl.style.cssText = 'flex: 1; display: flex; flex-direction: column; width: 100%;';
    this.appEl.appendChild(mainEl);

    if (this.currentView === 'lobby') {
      SeoService.setDefault();
      this.renderLobby(mainEl);
    } else if (this.currentView === 'niche') {
      SeoService.updateMeta('Niş & Labs', 'Geliştirme aşamasındaki en ilginç ve deneysel oyunları keşfet.');
      NicheHubView.render(mainEl, (gameId) => {
        this.startGameFlow(gameId);
      });
    } else if (this.currentView === 'leaderboard') {
      SeoService.updateMeta('Lider Tablosu', 'En iyi oyuncuların skorlarını gör ve yarışa katıl.');
      this.loadRecentGames();
      LeaderboardView.render(mainEl);
    } else if (this.currentView === 'profile') {
      SeoService.updateMeta('Profilim', 'Oyun başarımların, son skorların ve istatistiklerin.');
      ProfileView.render(mainEl);
    } else if (this.currentView === 'privacy') {
      SeoService.updateMeta('Gizlilik Politikası', 'Arcade Portal gizlilik ve veri işleme politikası.');
      PrivacyView.render(mainEl);
    } else if (this.currentView === 'admin') {
      SeoService.updateMeta('Admin Paneli', 'Yönetim paneli');
      AdminView.render(mainEl);
    }

    // Footer
    const footerEl = document.createElement('footer');
    footerEl.style.cssText = 'padding: 24px; text-align: center; color: #64748b; font-size: 0.85rem; border-top: 1px solid #334155; margin-top: auto;';
    footerEl.innerHTML = `
      <div style="display: flex; justify-content: center; gap: 20px; margin-bottom: 10px;">
        <a href="javascript:void(0)" id="footer-privacy-link" style="color: #818cf8;">Gizlilik Politikası</a>
        <span>&bull;</span>
        <span>AdSense Destekli Arcade</span>
      </div>
      Arcade Portal &copy; 2026 — Vite + Dynamic Import Architecture
    `;
    this.appEl.appendChild(footerEl);

    footerEl.querySelector('#footer-privacy-link')?.addEventListener('click', () => {
      this.currentView = 'privacy';
      this.render();
    });
  }

  private renderLobby(container: HTMLElement): void {
    if (!BackgroundRadio.userMuted) {
      BackgroundRadio.play();
    }

    const uniqueTags = Array.from(new Set(GAME_REGISTRY.flatMap(g => g.tags))).filter(t => t !== 'popular' && t !== 'niche');

    let filteredGames = GAME_REGISTRY.filter((game) => {
      const matchesCategory = this.selectedCategory === 'all' || 
                              (this.selectedCategory === 'popular' && game.tags.includes('popular')) || 
                              (this.selectedCategory !== 'all' && this.selectedCategory !== 'popular' && game.tags.includes(this.selectedCategory));
      const matchesSearch = game.title.toLowerCase().includes(this.searchQuery.toLowerCase()) || game.description.toLowerCase().includes(this.searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    if (this.sortBy === 'alphabetical') {
      filteredGames.sort((a, b) => a.title.localeCompare(b.title));
    }

    const totalPages = Math.ceil(filteredGames.length / this.catalogPageSize);
    const startIdx = (this.catalogPage - 1) * this.catalogPageSize;
    const paginatedGames = filteredGames.slice(startIdx, startIdx + this.catalogPageSize);

    const renderGridHTML = () => `
      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 20px;">
        ${paginatedGames.map(game => `
          <div class="glass-panel" style="padding: 20px; display: flex; flex-direction: column; justify-content: space-between; transition: transform 0.2s ease, border-color 0.2s ease;" onmouseover="this.style.transform='translateY(-4px)'; this.style.borderColor='#6366f1'" onmouseout="this.style.transform='none'; this.style.borderColor='rgba(255, 255, 255, 0.1)'">
            <div>
              <div style="height: 170px; background: #0f172a; border-radius: 10px; overflow: hidden; margin-bottom: 14px; border: 1px solid #334155;">
                <img src="${game.thumbnail}" alt="${game.title}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null; this.src='/assets/arcade_fallback.png';"/>
              </div>
              <h3 style="font-size: 1.25rem; margin-bottom: 6px;">${game.title}</h3>
              <p style="color: #94a3b8; font-size: 0.85rem; line-height: 1.4; margin-bottom: 18px;">${game.description}</p>
            </div>
            <button data-game-id="${game.id}" class="play-btn-animated">▶ OYNA</button>
          </div>
        `).join('')}
      </div>
      
      ${totalPages > 1 ? `
        <div style="display: flex; justify-content: center; gap: 12px; margin-top: 30px; align-items: center;">
          <button id="prev-page-btn" ${this.catalogPage === 1 ? 'disabled' : ''} style="padding: 8px 16px; border-radius: 6px; background: #334155; color: #f8fafc; border: none; font-weight: 600; cursor: ${this.catalogPage === 1 ? 'not-allowed' : 'pointer'}; opacity: ${this.catalogPage === 1 ? '0.5' : '1'}; transition: background 0.2s;">Önceki</button>
          <span style="color: #cbd5e1; font-size: 0.95rem; font-weight: 600;">Sayfa ${this.catalogPage} / ${totalPages}</span>
          <button id="next-page-btn" ${this.catalogPage === totalPages ? 'disabled' : ''} style="padding: 8px 16px; border-radius: 6px; background: #6366f1; color: #fff; border: none; font-weight: 600; cursor: ${this.catalogPage === totalPages ? 'not-allowed' : 'pointer'}; opacity: ${this.catalogPage === totalPages ? '0.5' : '1'}; transition: background 0.2s;">Sonraki</button>
        </div>
      ` : ''}
    `;

    const friends: import('./services/FriendService').Friend[] = [];
    FriendService.getFriends().then(list => {
      const el = document.getElementById('friend-count-badge');
      if (el) el.textContent = String(list.length);
    });

    container.innerHTML = `
      <div class="ad-slot-container ad-slot-container--banner" style="max-width: 1000px; margin: 20px auto 0 auto;">
        <span style="color: #64748b; font-size: 0.85rem;">[ Banner Reklam Alanı — 728x90 ]</span>
      </div>

      <div class="main-grid-layout">
        
        <div style="display: flex; flex-direction: column; gap: 20px;">
          <div class="glass-panel" style="padding: 20px;">
            <h3 style="font-size: 1.05rem; font-weight: 800; margin-bottom: 14px; display: flex; align-items: center; gap: 6px;">
              <span>🕒</span> Son Oynananlar
            </h3>
            <div id="recent-games-list" style="display: flex; flex-direction: column; gap: 10px; font-size: 0.85rem;">
              <div style="text-align: center; color: #64748b;">Yükleniyor...</div>
            </div>
          </div>
        </div>

        <div style="display: flex; flex-direction: column;">
          <div class="glass-panel" style="padding: 24px; margin-bottom: 24px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 16px;">
              <div>
                <h2 style="font-size: 1.5rem; font-weight: 800; color: #f8fafc; display: flex; align-items: center; gap: 10px;">
                  🕹️ Oyun Kataloğu
                </h2>
                <p style="color: #94a3b8; font-size: 0.9rem; margin-top: 4px;">Dinamik yükleyici ile istediğin oyunu anında başlat.</p>
              </div>
              
              <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap; width: 100%;">
                <div style="position: relative; flex: 1; min-width: 150px;">
                  <span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b;">🔍</span>
                  <select id="extra-category-select" style="width: 100%; padding: 10px 16px 10px 36px; border-radius: 8px; background: #1e293b; border: 1px solid #475569; color: #f8fafc; outline: none; cursor: pointer; transition: all 0.2s;">
                    <option value="all" ${this.selectedCategory === 'all' ? 'selected' : ''}>Tüm Oyunlar</option>
                    <option value="popular" ${this.selectedCategory === 'popular' ? 'selected' : ''}>🔥 Popüler</option>
                    <option value="niche" ${this.selectedCategory === 'niche' ? 'selected' : ''}>🧪 Niş</option>
                    <optgroup label="Diğer">
                      ${uniqueTags.map(tag => `<option value="${tag}" ${this.selectedCategory === tag ? 'selected' : ''}>${tag.charAt(0).toUpperCase() + tag.slice(1)}</option>`).join('')}
                    </optgroup>
                  </select>
                </div>
                
                <div style="position: relative; flex: 1; min-width: 150px;">
                  <span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b;">↕️</span>
                  <select id="sort-select" style="width: 100%; padding: 10px 16px 10px 36px; border-radius: 8px; background: #1e293b; border: 1px solid #475569; color: #f8fafc; outline: none; cursor: pointer; transition: all 0.2s;">
                    <option value="popular" ${this.sortBy === 'popular' ? 'selected' : ''}>Sırala: Popüler</option>
                    <option value="alphabetical" ${this.sortBy === 'alphabetical' ? 'selected' : ''}>Sırala: A-Z</option>
                  </select>
                </div>
  
                <div style="position: relative; flex: 1; min-width: 150px;">
                  <input type="text" id="search-input" value="${this.searchQuery}" placeholder="Oyun ara..." style="width: 100%; padding: 10px 16px; border-radius: 8px; background: #1e293b; border: 1px solid #475569; color: #f8fafc; outline: none; transition: all 0.2s;">
                </div>
              </div>
            </div>
          </div>
          <div id="game-grid-container">
            ${renderGridHTML()}
          </div>
          
        </div>

        <div style="display: flex; flex-direction: column; gap: 24px;">
          <div class="glass-panel" style="padding: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px;">
              <h3 style="font-size: 1.05rem; font-weight: 800;">👥 Arkadaşlarım (${friends.length})</h3>
              <button id="open-friend-modal-btn2" style="font-size: 0.78rem; color: #818cf8; font-weight: 700;">+ Ekle</button>
            </div>
            <div style="display: flex; flex-direction: column; gap: 10px; font-size: 0.88rem;">
              ${friends.map(f => `
                <div style="display: flex; justify-content: space-between; align-items: center; color: #cbd5e1;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span style="width: 8px; height: 8px; border-radius: 50%; background: ${f.status === 'online' ? '#22c55e' : '#64748b'};"></span>
                    <span style="font-weight: 600;">${f.username}</span>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="glass-panel" style="padding: 0; overflow: hidden; display: flex; align-items: center; justify-content: center; background: #0f172a; min-height: 400px; border: 1px dashed #334155;">
            <div style="text-align: center; color: #475569; padding: 20px;">
              <span style="font-size: 1.5rem; display: block; margin-bottom: 10px;">AD</span>
              <span style="font-size: 0.75rem;">Google Ads<br/>Dikey Reklam Alanı</span>
            </div>
          </div>
        </div>
      </div>
    `;

    const bindPaginationAndPlay = () => {
      const prevBtn = document.getElementById('prev-page-btn');
      if (prevBtn && !prevBtn.hasAttribute('disabled')) {
        prevBtn.addEventListener('click', () => {
          if (this.catalogPage > 1) {
            this.catalogPage--;
            this.render();
          }
        });
      }

      const nextBtn = document.getElementById('next-page-btn');
      if (nextBtn && !nextBtn.hasAttribute('disabled')) {
        nextBtn.addEventListener('click', () => {
          if (this.catalogPage < totalPages) {
            this.catalogPage++;
            this.render();
          }
        });
      }

      const gridContainer = document.getElementById('game-grid-container');
      if (gridContainer) {
        gridContainer.querySelectorAll('.play-btn-animated').forEach((btn) => {
          btn.addEventListener('click', async (e) => {
            const target = e.currentTarget as HTMLButtonElement;
            const gameId = target.getAttribute('data-game-id');
            if (gameId) {
              await this.startGameFlow(gameId);
            }
          });
        });
      }
    };
    bindPaginationAndPlay();

    const searchInput = container.querySelector('#search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.catalogPage = 1;
      
      // Update DOM dynamically without losing focus
      filteredGames = GAME_REGISTRY.filter((game) => {
        const matchesCategory = this.selectedCategory === 'all' || 
                                (this.selectedCategory === 'popular' && game.tags.includes('popular')) || 
                                (this.selectedCategory !== 'all' && this.selectedCategory !== 'popular' && game.tags.includes(this.selectedCategory));
        const matchesSearch = game.title.toLowerCase().includes(this.searchQuery.toLowerCase()) || game.description.toLowerCase().includes(this.searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
      });
      if (this.sortBy === 'alphabetical') {
        filteredGames.sort((a, b) => a.title.localeCompare(b.title));
      }
      
      const gContainer = document.getElementById('game-grid-container');
      if (gContainer) {
        gContainer.innerHTML = renderGridHTML();
        bindPaginationAndPlay();
      }
    });

    const extraSelect = document.getElementById('extra-category-select') as HTMLSelectElement;
    if (extraSelect) {
      extraSelect.addEventListener('change', (e) => {
        const val = (e.target as HTMLSelectElement).value;
        if (val) {
          this.selectedCategory = val;
          this.render();
        }
      });
    }

    const sortSelect = document.getElementById('sort-select') as HTMLSelectElement;
    sortSelect?.addEventListener('change', (e) => {
      this.sortBy = (e.target as HTMLSelectElement).value as any;
      this.render();
    });

    // Connect Friend Modal Button
    container.querySelector('#open-friend-modal-btn2')?.addEventListener('click', () => {
      FriendModal.show();
    });

    // Connect play buttons
    container.querySelectorAll('[data-launch-niche]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-launch-niche');
        if (id) {
          const game = GAME_REGISTRY.find((g) => g.id === id);
          ToastManager.show(`🎮 '${game ? game.title : id}' oyunu başlatılıyor...`, 'success');
          this.startGameFlow(id);
        }
      });
    });
    
    // Connect play buttons (original)
    container.querySelectorAll('.play-btn-animated').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const gameId = target.getAttribute('data-game-id');
        if (gameId) {
          await this.startGameFlow(gameId);
        }
      });
    });

    this.loadRecentGames();
  }

  private async loadRecentGames(): Promise<void> {
    const listEl = document.getElementById('recent-games-list');
    if (!listEl) return;
    
    if (!AuthStore.isAuthenticated) {
      listEl.innerHTML = `<div style="text-align:center;color:#64748b;font-size:0.85rem;padding:10px;">Giriş yaparak son oyunlarınızı görebilirsiniz.</div>`;
      return;
    }

    const res = await ApiClient.getUserScores(AuthStore.getUser()!.id);
    let recentScores = res.data;
    
    // Fallback if backend is down
    if (res.error || !recentScores) {
      console.warn('Backend error or down, using mock recent games.');
      recentScores = [
        { game_id: 'snake', score: 100 },
        { game_id: 'tetris', score: 5000 },
        { game_id: 'pacman', score: 1200 }
      ] as any;
    }

    if (!res.data || res.data.length === 0) {
      listEl.innerHTML = `<div style="text-align:center;color:#64748b;font-size:0.85rem;padding:10px;">Henüz oyun oynamadınız.</div>`;
      return;
    }

    // Get unique game IDs from recent scores, limit to 3
    const safeScores = recentScores || [];
    const recentIds = Array.from(new Set(safeScores.map(s => s.game_id))).slice(0, 3);
    
    listEl.innerHTML = recentIds.map(id => {
      const g = GAME_REGISTRY.find(game => game.id === id);
      if (!g) return '';
      return `
        <div data-recent-game="${g.id}" style="display: flex; align-items: center; gap: 12px; padding: 8px; border-radius: 8px; background: rgba(30, 41, 59, 0.5); cursor: pointer; transition: background 0.2s;" onmouseover="this.style.background='rgba(51, 65, 85, 0.8)'" onmouseout="this.style.background='rgba(30, 41, 59, 0.5)'">
          <img src="${g.thumbnail}" alt="${g.title}" style="width: 32px; height: 32px; border-radius: 6px; object-fit: cover; border: 1px solid #475569;" onerror="this.onerror=null; this.src='/assets/arcade_fallback.png';">
          <div style="flex: 1; overflow: hidden;">
            <div style="color: #e2e8f0; font-size: 0.85rem; font-weight: 600; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">${g.title}</div>
            <div style="color: #818cf8; font-size: 0.7rem;">▶ Oyna</div>
          </div>
        </div>
      `;
    }).join('');

    // Attach click listeners to dynamically created recent games
    listEl.querySelectorAll('[data-recent-game]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-recent-game');
        if (id) {
          const game = GAME_REGISTRY.find((g) => g.id === id);
          ToastManager.show(`🎮 '${game ? game.title : id}' oyunu başlatılıyor...`, 'success');
          this.startGameFlow(id);
        }
      });
    });
  }

  private async startGameFlow(gameId: string): Promise<void> {
    this.activeGameId = gameId;
    
    // Task 10: Pause background music during gameplay
    BackgroundRadio.pause();

    // 1) Pre-game Interstitial Reklam Göster (4 sn geri sayım + Skip)
    await InterstitialAd.show(gameId);

    // Update SEO
    const activeMeta = GAME_REGISTRY.find((g) => g.id === gameId);
    if (activeMeta) SeoService.setGameMeta(activeMeta.title, activeMeta.description);

    // 2) Oyun görünümüne geç
    this.currentView = 'game';
    this.render();

    // 3) Canvas'a dinamik oyunu yükle
    const targetContainer = document.getElementById('game-container');
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    
    if (canvas && targetContainer) {
      const gameInstance = await gameLoader.load(gameId, canvas);
      scoreValidator.startSession(gameId);
      AnalyticsService.startSession(gameId);
      FriendService.updatePresence('in_game', gameId);

      // Check if mobile device
      if (window.innerWidth <= 768) {
        import('./ui/components/MobileControls').then(({ MobileControls }) => {
          MobileControls.render(gameInstance, targetContainer, () => {
            gameLoader.unload();
            this.currentView = 'lobby';
            this.render();
          });
        });
        
        // Hide normal overlay for mobile, assume they are ready
        const overlay = document.getElementById('game-overlay');
        if (overlay) overlay.style.display = 'none';
        
      } else {
        // Pause game and show "Click to Play" overlay instead of starting immediately in background
        const overlay = document.getElementById('game-overlay');
        if (overlay) {
          gameLoader.pause();
          overlay.style.display = 'flex';
          overlay.innerHTML = `
            <div style="background:rgba(15,23,42,0.9); padding:20px 40px; border-radius:12px; border:2px solid #334155; text-align:center; animation: popIn 0.3s ease-out;">
              <h2 style="color:#f8fafc; font-size:1.8rem; margin-bottom:12px;">▶️ Oyuna Başla</h2>
              <p style="color:#cbd5e1; font-size:1.1rem;">Oyunu başlatmak için tıklayın.</p>
            </div>
          `;
          overlay.onclick = () => {
            gameLoader.resume();
            overlay.style.display = 'none';
          };
        }
      }

      // 4) Oyun Kontrol Çubuğunu ve D-Pad'i başlat
      const controlsContainer = document.getElementById('game-controls-container');
      if (controlsContainer) {
        this.gameControls = new GameControls(controlsContainer, {
          onPauseToggle: () => {
            const isPaused = (gameLoader as any).isPaused;
            if (isPaused) {
              gameLoader.resume();
            } else {
              gameLoader.pause();
            }
          },
          onRestart: async () => {
            await gameLoader.load(gameId, canvas);
            scoreValidator.startSession(gameId);
            ToastManager.show('Oyun yeniden başlatıldı.', 'info');
          },
        });
        this.gameControls.render();
      }
    }
  }

  private renderGameView(): void {
    const activeMeta = GAME_REGISTRY.find((g) => g.id === this.activeGameId) || GAME_REGISTRY[0];

    const mockGameHighScores = [
      { rank: 1, user: 'PixelKing', score: activeMeta.id === 'tetris' ? 14500 : activeMeta.id === 'snake' ? 1450 : 890 },
      { rank: 2, user: 'RetroMaster', score: activeMeta.id === 'tetris' ? 12400 : activeMeta.id === 'snake' ? 1200 : 750 },
      { rank: 3, user: 'CyberKnight', score: activeMeta.id === 'tetris' ? 9800 : activeMeta.id === 'snake' ? 980 : 620 },
      { rank: 4, user: 'ArcadePro', score: activeMeta.id === 'tetris' ? 8200 : activeMeta.id === 'snake' ? 850 : 510 },
      { rank: 5, user: 'GamerX', score: activeMeta.id === 'tetris' ? 6500 : activeMeta.id === 'snake' ? 700 : 430 },
    ];

    this.appEl.innerHTML = `
      <header style="padding: 16px 30px; background: #1e293b; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center;">
        <div style="display: flex; align-items: center; gap: 16px;">
          <button id="back-to-lobby-btn" style="padding: 8px 18px; background: #334155; color: #f8fafc; border-radius: 8px; font-weight: 600; transition: background 0.2s;">
            &larr; Ana Sayfaya Dön
          </button>
        </div>
        <h2 style="font-size: 1.3rem; font-weight: 800;" class="gradient-text">${activeMeta.title}</h2>
        <div style="font-size: 0.85rem; color: #94a3b8;">Kısayollar: [M] Ses &bull; [F] Tam Ekran &bull; [P] Pause &bull; [R] Restart</div>
      </header>

      <!-- 3-Column Arcade View Layout (Only visible in normal mode) -->
      <div class="game-view-layout">
        
        <!-- Sol Kenar: Hızlı Oyun Seçim Listesi (Quick Switcher) -->
        <aside class="game-sidebar-left glass-panel" style="overflow-y: auto; max-height: calc(100vh - 80px);">
          <h3 style="font-size: 0.95rem; font-weight: 700; margin-bottom: 8px; color: #f8fafc;">🎮 Ana Arcade Oyunlar</h3>
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px;">
            ${GAME_REGISTRY.filter(g => !g.tags.includes('niche')).map(g => `
              <div data-switch-game="${g.id}" class="quick-game-item ${g.id === this.activeGameId ? 'quick-game-item--active' : ''}">
                <img src="${g.thumbnail}" alt="" style="width: 36px; height: 36px; border-radius: 8px; object-fit: cover; border: 1px solid #334155;">
                <div style="overflow: hidden;">
                  <div class="quick-game-item__title" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${g.title}</div>
                  <span style="font-size: 0.72rem; color: #818cf8;">▶ Oyna</span>
                </div>
              </div>
            `).join('')}
          </div>

          <h3 style="font-size: 0.95rem; font-weight: 700; margin-bottom: 8px; color: #f8fafc;">🧪 Niş & Deneysel Oyunlar</h3>
          <div style="display: flex; flex-direction: column; gap: 8px;">
            ${GAME_REGISTRY.filter(g => g.tags.includes('niche')).map(g => `
              <div data-switch-game="${g.id}" class="quick-game-item ${g.id === this.activeGameId ? 'quick-game-item--active' : ''}">
                <img src="${g.thumbnail}" alt="" style="width: 36px; height: 36px; border-radius: 8px; object-fit: cover; border: 1px solid #334155;">
                <div style="overflow: hidden;">
                  <div class="quick-game-item__title" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${g.title}</div>
                  <span style="font-size: 0.72rem; color: #a855f7;">▶ Test Et</span>
                </div>
              </div>
            `).join('')}
          </div>
        </aside>

        <!-- Orta Alan: Canvas & Kontroller & Banner Reklam -->
        <div class="game-container-wrapper" style="flex: 1; display: flex; flex-direction: column; align-items: center;">
          
          <div id="game-fullscreen-wrapper" style="width: 100%; display: flex; flex-direction: column; align-items: center; position: relative;">
            
            ${DuelService.activeDuel ? `
              <!-- Duel Live Score HUD -->
              <div style="width:100%; max-width:800px; display:flex; justify-content:space-between; align-items:center; background:linear-gradient(90deg, #1e293b, #0f172a, #1e293b); padding:10px 20px; border-radius:12px 12px 0 0; border:2px solid #e11d48; border-bottom:none; margin-bottom:0;">
                <div style="display:flex; flex-direction:column; align-items:flex-start;">
                  <span style="font-size:0.75rem; color:#94a3b8; text-transform:uppercase; font-weight:800;">Sen</span>
                  <span id="duel-my-score" style="font-size:1.8rem; font-weight:900; color:#38bdf8;">0</span>
                </div>
                
                <div style="display:flex; flex-direction:column; align-items:center; animation: pulse 2s infinite;">
                  <span style="font-size:1.5rem;">⚔️</span>
                  <span style="font-size:0.7rem; color:#ef4444; font-weight:800; letter-spacing:1px;">DÜELLO</span>
                </div>
                
                <div style="display:flex; flex-direction:column; align-items:flex-end;">
                  <span style="font-size:0.75rem; color:#94a3b8; text-transform:uppercase; font-weight:800;">Rakip</span>
                  <span id="duel-opp-score" style="font-size:1.8rem; font-weight:900; color:#f43f5e;">${DuelService.opponentScore}</span>
                </div>
              </div>
            ` : ''}

            <div id="game-container" class="game-center-area">
              <div class="game-canvas-container" style="position: relative;">
                <canvas id="game-canvas"></canvas>
                <div id="game-overlay" class="game-overlay"></div>
              </div>
            </div>
            <div id="game-controls-container" style="width: 100%; max-width: 800px; margin-top: 20px;"></div>
          </div>

          <!-- Puanlama ve Yorumlar Bölümü -->
          <div style="width: 100%; max-width: 800px; margin-top: 30px; display: flex; flex-direction: column; gap: 20px; margin-bottom: 40px;">
            
            <!-- Rating Section -->
            <div class="glass-panel" style="padding: 20px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
              <div>
                <h3 style="font-size: 1.1rem; font-weight: 700; color: #f8fafc; margin-bottom: 4px;">Oyun Değerlendirmesi</h3>
                <div id="game-avg-rating" style="color: #cbd5e1; font-size: 0.9rem;">Ortalama Puan: Yükleniyor...</div>
              </div>
              <div id="user-rating-stars" style="display: flex; gap: 8px; font-size: 1.5rem; cursor: pointer;">
                <span style="color: #64748b; font-size: 0.9rem;">(Yükleniyor)</span>
              </div>
            </div>

            <!-- Comments Section -->
            <div class="glass-panel" style="padding: 20px;">
              <h3 style="font-size: 1.1rem; font-weight: 700; color: #f8fafc; margin-bottom: 16px;">Yorumlar & Taktikler</h3>
              
              <div id="comment-input-area" style="margin-bottom: 20px;">
                <textarea id="new-comment-text" rows="3" placeholder="Bu oyun hakkında bir taktik veya düşünce paylaş..." style="width: 100%; padding: 12px; border-radius: 8px; background: #1e293b; color: #f8fafc; border: 1px solid #475569; font-family: inherit; font-size: 0.9rem; resize: vertical; margin-bottom: 8px;"></textarea>
                <div style="display: flex; justify-content: flex-end;">
                  <button id="submit-comment-btn" style="padding: 8px 16px; background: #6366f1; color: #fff; border-radius: 6px; border: none; font-weight: 600; cursor: pointer; transition: background 0.2s;">Gönder</button>
                </div>
              </div>

              <div id="comments-list" style="display: flex; flex-direction: column; gap: 12px; max-height: 400px; overflow-y: auto; padding-right: 8px;">
                <div style="text-align: center; color: #64748b; font-size: 0.9rem;">Yorumlar yükleniyor...</div>
              </div>
            </div>
          </div>
          
          <div class="ad-slot-container ad-slot-container--banner" style="width: 100%;">
            <span style="color: #64748b; font-size: 0.85rem;">[ Oyun Altı Banner Reklam — 728x90 ]</span>
          </div>
        </div>

        <!-- Sağ Kenar: İpuçları, Top 10 Skor & Display Reklam -->
        <aside class="game-sidebar-right">
          <!-- Oyun İpuçları & Kontroller -->
          <div class="glass-panel" style="padding: 16px; margin-bottom: 20px;">
            <h3 style="font-size: 0.95rem; font-weight: 700; color: #f8fafc; margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
              <span>💡</span> Oyun İpuçları
            </h3>
            <div style="font-size: 0.8rem; color: #cbd5e1; display: flex; flex-direction: column; gap: 8px;">
              <p style="color: #e2e8f0; font-size: 0.85rem; line-height: 1.4; margin-bottom: 8px; border-bottom: 1px solid #334155; padding-bottom: 8px;">
                ${activeMeta.description}
              </p>
              <h5 style="color: #94a3b8; font-size: 0.75rem; font-weight: 700; margin: 4px 0 0 0; text-transform: uppercase;">Kontroller</h5>
              ${(activeMeta.controls || [
                { keys: ['Yön Tuşları'], action: 'Hareket' },
                { keys: ['Z / X / C'], action: 'Aksiyon' },
                { keys: ['Enter'], action: 'Başla' }
              ]).map(c => `
              <div style="display: flex; gap: 8px; align-items: center;">
                <kbd style="background: #334155; padding: 2px 6px; border-radius: 4px; border: 1px solid #475569; font-size: 0.75rem;">${c.keys.join(' / ')}</kbd>
                <span>${c.action}</span>
              </div>`).join('')}
              <p style="color: #94a3b8; font-style: italic; margin-top: 8px; font-size: 0.75rem;">💡 Oyun içinde klavye kontrolleri aktif değilse oyun ekranına tıklayın. Oyunu durdurmak için P, yeniden başlatmak için R tuşuna basabilirsiniz.</p>
            </div>
          </div>

          <div class="glass-panel high-scores-widget">
            <h3 style="font-size: 0.95rem; font-weight: 700; color: #f8fafc;">🏆 Top 10 Skor (${activeMeta.title})</h3>
            <div class="high-scores-list">
              ${mockGameHighScores
                .map(
                  (s) => `
                <div class="high-score-item">
                  <span class="high-score-item__rank">#${s.rank}</span>
                  <span class="high-score-item__user">${s.user}</span>
                  <span class="high-score-item__score">${s.score.toLocaleString()}</span>
                </div>
              `
                )
                .join('')}
            </div>
          </div>

          <div class="ad-slot-container ad-slot-container--sidebar" style="min-height: 250px;">
            <span style="color: #64748b; font-size: 0.85rem; text-align: center; padding: 16px;">
              [ Sidebar Reklam — 300x250 ]
            </span>
          </div>
        </aside>

      </div>
    `;

    // Connect Back Button
    document.getElementById('back-to-lobby-btn')?.addEventListener('click', async () => {
      AnalyticsService.endSession();
      await gameLoader.unload();
      scoreValidator.endSession();
      FriendService.updatePresence('online', 'lobby');
      this.currentView = 'lobby';
      this.activeGameId = null;
      this.render();
    });



    // Connect Quick Switcher Items
    document.querySelectorAll('[data-switch-game]').forEach((item) => {
      item.addEventListener('click', async (e) => {
        const nextId = (e.currentTarget as HTMLElement).getAttribute('data-switch-game');
        if (nextId && nextId !== this.activeGameId) {
          await gameLoader.unload();
          scoreValidator.endSession();
          await this.startGameFlow(nextId);
        }
      });
    });

    if (this.activeGameId) {
      this.loadGameRatingsAndComments(this.activeGameId);
    }
  }

  private async loadGameRatingsAndComments(gameId: string) {
    const avgEl = document.getElementById('game-avg-rating');
    const starsEl = document.getElementById('user-rating-stars');
    const listEl = document.getElementById('comments-list');
    const submitBtn = document.getElementById('submit-comment-btn') as HTMLButtonElement;
    const commentInput = document.getElementById('new-comment-text') as HTMLTextAreaElement;

    // --- RATINGS ---
    if (avgEl && starsEl) {
      const ratingRes = await ApiClient.getGameRating(gameId);
      if (ratingRes.data) {
        const { average, count } = ratingRes.data;
        avgEl.innerHTML = `Ortalama Puan: <strong style="color:#fbbf24;">${average.toFixed(1)}</strong> <span style="font-size:0.8rem;color:#64748b;">(${count} oy)</span>`;
      }

      if (!AuthStore.isAuthenticated) {
        starsEl.innerHTML = `<span style="color:#64748b;font-size:0.9rem;">Oy vermek için giriş yapın</span>`;
      } else {
        // Render 5 stars for voting
        let starsHTML = '';
        for (let i = 1; i <= 5; i++) {
          starsHTML += `<span data-star="${i}" style="color: #475569; transition: color 0.2s;">★</span>`;
        }
        starsEl.innerHTML = starsHTML;

        starsEl.querySelectorAll('span').forEach((star) => {
          star.addEventListener('mouseover', (e) => {
            const val = parseInt((e.target as HTMLElement).getAttribute('data-star') || '0');
            starsEl.querySelectorAll('span').forEach(s => {
              if (parseInt(s.getAttribute('data-star') || '0') <= val) {
                s.style.color = '#fbbf24';
              } else {
                s.style.color = '#475569';
              }
            });
          });
          star.addEventListener('mouseout', () => {
            starsEl.querySelectorAll('span').forEach(s => s.style.color = '#475569');
          });
          star.addEventListener('click', async (e) => {
            const val = parseInt((e.target as HTMLElement).getAttribute('data-star') || '0');
            if (val > 0) {
              await ApiClient.submitRating(AuthStore.getUser()!.id, gameId, val);
              ToastManager.show(`Oyun için ${val} yıldız verdiniz!`, 'success');
              this.loadGameRatingsAndComments(gameId); // reload
            }
          });
        });
      }
    }

    // --- COMMENTS ---
    if (listEl && submitBtn && commentInput) {
      if (!AuthStore.isAuthenticated) {
        commentInput.disabled = true;
        commentInput.placeholder = 'Yorum yapmak için giriş yapmalısınız.';
        submitBtn.disabled = true;
        submitBtn.style.opacity = '0.5';
      } else {
        submitBtn.onclick = async () => {
          const txt = commentInput.value.trim();
          if (!txt) return;
          submitBtn.disabled = true;
          submitBtn.textContent = '...';
          const res = await ApiClient.postComment(AuthStore.getUser()!.id, gameId, txt);
          if (res.error) {
            ToastManager.show('Yorum gönderilemedi.', 'error');
          } else {
            commentInput.value = '';
            ToastManager.show('Yorum eklendi!', 'success');
            this.loadGameRatingsAndComments(gameId);
          }
          submitBtn.disabled = false;
          submitBtn.textContent = 'Gönder';
        };
      }

      const commentsRes = await ApiClient.getComments(gameId);
      if (commentsRes.error) {
        listEl.innerHTML = `<div style="text-align:center;color:#ef4444;">Yorumlar yüklenemedi.</div>`;
      } else if (!commentsRes.data || commentsRes.data.length === 0) {
        listEl.innerHTML = `<div style="text-align:center;color:#64748b;font-size:0.9rem;">Henüz yorum yapılmamış. İlk yorumu sen yap!</div>`;
      } else {
        listEl.innerHTML = commentsRes.data.map(c => `
          <div style="background: rgba(30, 41, 59, 0.6); border: 1px solid #334155; border-radius: 8px; padding: 12px; display: flex; gap: 12px;">
            <img src="/assets/avatars/${c.avatar_id || 'av_1'}.png" onerror="this.src='/assets/avatars/av_3.png'" style="width:36px;height:36px;border-radius:50%;object-fit:cover;border:1px solid #475569;">
            <div style="flex: 1;">
              <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
                <strong style="color:#e2e8f0;font-size:0.9rem;">${c.username || 'Oyuncu'}</strong>
                <span style="color:#64748b;font-size:0.75rem;">${new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <div style="color:#cbd5e1;font-size:0.9rem;line-height:1.4;white-space:pre-wrap;">${c.comment}</div>
            </div>
          </div>
        `).join('');
      }
    }
  }

  private showBugReportModal(): void {
    const existing = document.getElementById('bug-report-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'bug-report-modal';
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(15, 23, 42, 0.85); display: flex; align-items: center; justify-content: center;
      z-index: 10000; backdrop-filter: blur(4px);
    `;

    modal.innerHTML = `
      <div style="background: #1e293b; padding: 30px; border-radius: 12px; border: 1px solid #475569; width: 90%; max-width: 450px; text-align: left; box-shadow: 0 10px 25px rgba(0,0,0,0.5);">
        <h2 style="font-size: 1.4rem; font-weight: 800; color: #f8fafc; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
          🐛 Hata veya Öneri Bildir
        </h2>
        <p style="color: #94a3b8; font-size: 0.9rem; margin-bottom: 20px;">Karşılaştığınız bir sorunu veya fikrinizi yazın. Geliştirici ekibimiz inceleyecektir.</p>
        
        <textarea id="bug-text" rows="4" placeholder="Oyun takıldı, ses gelmedi veya harika bir fikrim var..." style="width: 100%; padding: 12px; border-radius: 8px; background: #0f172a; color: #f8fafc; border: 1px solid #475569; font-family: inherit; font-size: 0.9rem; resize: vertical; margin-bottom: 16px; outline: none;"></textarea>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="cancel-bug-btn" style="padding: 10px 16px; background: transparent; color: #cbd5e1; border: 1px solid #475569; border-radius: 8px; font-weight: 600; cursor: pointer;">İptal</button>
          <button id="submit-bug-btn" style="padding: 10px 16px; background: #ef4444; color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;">Bildirimi Gönder</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    modal.querySelector('#cancel-bug-btn')?.addEventListener('click', () => modal.remove());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });

    modal.querySelector('#submit-bug-btn')?.addEventListener('click', async () => {
      const text = (modal.querySelector('#bug-text') as HTMLTextAreaElement).value.trim();
      if (!text) {
        ToastManager.show('Lütfen bir mesaj yazın.', 'warning');
        return;
      }
      
      const user = AuthStore.getUser();
      const btn = modal.querySelector('#submit-bug-btn') as HTMLButtonElement;
      btn.textContent = 'Gönderiliyor...';
      btn.disabled = true;

      const res = await ApiClient.submitFeedback(user?.id, this.activeGameId || 'lobby', text);
      if (res.error) {
        ToastManager.show('Gönderilemedi, lütfen tekrar deneyin.', 'error');
        btn.textContent = 'Bildirimi Gönder';
        btn.disabled = false;
      } else {
        ToastManager.show('Geri bildiriminiz başarıyla iletildi, teşekkürler!', 'success');
        modal.remove();
      }
    });
  }

  private showGuestScoreModal(score: number): void {
    const existing = document.getElementById('guest-score-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'guest-score-modal';
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(15, 23, 42, 0.85); display: flex; align-items: center; justify-content: center;
      z-index: 10000; backdrop-filter: blur(4px);
    `;

    modal.innerHTML = `
      <div style="background: #1e293b; padding: 30px; border-radius: 12px; border: 1px solid #475569; width: 90%; max-width: 450px; text-align: center; box-shadow: 0 10px 25px rgba(0,0,0,0.5);">
        <h2 style="font-size: 1.8rem; font-weight: 800; color: #fbbf24; margin-bottom: 12px;">Skorunu Kaybetme!</h2>
        <p style="color: #f8fafc; font-size: 1.2rem; margin-bottom: 8px;">Harika bir iş çıkardın: <strong>${score} Puan!</strong></p>
        <p style="color: #94a3b8; font-size: 0.95rem; margin-bottom: 24px;">Hemen kayıt ol veya giriş yap, rekorun dünya sıralamasına eklensin. Skorun askıda bekliyor!</p>
        
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button id="guest-cancel-btn" style="padding: 10px 20px; background: transparent; color: #cbd5e1; border: 1px solid #475569; border-radius: 8px; font-weight: 600; cursor: pointer;">Sil</button>
          <button id="guest-login-btn" style="padding: 10px 24px; background: #6366f1; color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; transition: 0.2s;">Giriş Yap / Kayıt Ol</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    modal.querySelector('#guest-cancel-btn')?.addEventListener('click', () => {
      this.pendingScore = null;
      modal.remove();
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.pendingScore = null;
        modal.remove();
      }
    });

    modal.querySelector('#guest-login-btn')?.addEventListener('click', () => {
      modal.remove();
      // AuthModal tetikleyicisi (static method)
      import('./ui/components/AuthModal').then(({ AuthModal }) => {
        AuthModal.show('login');
      });
    });
  }
}

const app = new App();
app.init();


